import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { CAMPOS_USUARIO, paraApi, valoresIniciais } from './camposUsuario';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { api } from '../../lib/api';
import CampoSenha from '../../components/CampoSenha';
import { useSenhaPadrao } from '../../lib/senhaPadrao';

// Rótulo de cada módulo pra agrupar as "ações" na tela — mesmas chaves de src/permissoes.js `ACOES`.
const MODULOS_ACAO = [
  { chave: 'imoveis', rotulo: 'Imóveis' },
  { chave: 'clientes', rotulo: 'Clientes' },
  { chave: 'leads', rotulo: 'Leads' },
  { chave: 'pedidos', rotulo: 'Pedidos' },
  { chave: 'usuarios', rotulo: 'Usuários' },
];

// `ehNovo` é prop explícita (vem do `publicId` da URL em PaginaUsuario.js)
// — achado em auditoria 2026-09-18: antes era deduzido de
// `!usuarioInicial`, e se o carregamento do usuário falhasse (sem
// `.catch` na página, já corrigido), a tela de EDIÇÃO virava "criação"
// sem avisar (mostrava seletor de Nível e aviso de senha "123mudar", e
// salvava via POST). Nunca deduzir "é criação?" de um dado que pode vir
// vazio por causa de erro — sempre da URL/rota.
//
// `empresaIdPreSelecionada` (achado em auditoria 2026-09-19, análise do
// Laravel legado): quando o superadmin cria usuário a partir da lista
// GLOBAL (sem empresa escolhida antes), o form precisa: (1) oferecer
// "Superadmin" como opção de nível (não existia — a rota nem aceitava
// isso antes), e (2) se o nível escolhido for Admin/Corretor, pedir QUAL
// empresa, já que não veio da URL.
export default function FormUsuario({ ehNovo, usuarioInicial, empresaIdPreSelecionada, aoSalvar }) {
  const { usuario: usuarioLogado } = useAuth();
  const toast = useToast();
  const [valores, setValores] = useState(() => valoresIniciais(usuarioInicial));
  const [empresas, setEmpresas] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const { senhaPadrao, senhaMinima } = useSenhaPadrao();
  const [novaSenha, setNovaSenha] = useState('');
  const [trocarNoProximo, setTrocarNoProximo] = useState(true);
  const [definindoSenha, setDefinindoSenha] = useState(false);

  async function definirNovaSenha() {
    setDefinindoSenha(true);
    try {
      await api.post(`/usuarios/${usuarioInicial.public_id}/definir-senha`, { senha: novaSenha, trocarNoProximoAcesso: trocarNoProximo });
      toast.sucesso('Senha definida.');
      setNovaSenha('');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível definir a senha');
    } finally {
      setDefinindoSenha(false);
    }
  }
  // Permissões por usuário: os 3 "recursos" originais (nasce tudo desligado) + as "ações" por
  // módulo (22/10/2026) — essas, diferente dos recursos, têm um PADRÃO que replica o que o
  // usuário já podia fazer antes dessa tela existir (ver src/permissoes.js `ACOES`), senão um
  // usuário antigo apareceria com tudo desmarcado mesmo continuando com acesso total por padrão.
  // Superadmin não tem nada disso a configurar: acesso total sempre.
  const [recursos, setRecursos] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [permissoes, setPermissoes] = useState(() => usuarioInicial?.permissoes || {});
  const [perfilSelecionado, setPerfilSelecionado] = useState('');
  const ehSuperadminAlvo = ehNovo ? valores.nivel === '1' : usuarioInicial?.nivel === 1;
  // As "ações" só valem de verdade pro nivel 3 (nivel 1/2 sempre têm acesso total no servidor,
  // mostrar as caixinhas pra eles seria mentira — não têm efeito nenhum).
  const nivelAlvo = ehNovo ? Number(valores.nivel) : usuarioInicial?.nivel;
  const mostrarAcoes = !ehSuperadminAlvo && nivelAlvo === 3;

  useEffect(() => {
    api
      .get('/permissoes')
      .then((r) => {
        setRecursos(r.data.recursos);
        setAcoes(r.data.acoes);
        setPerfis(r.data.perfis);
        // Preenche as chaves de AÇÃO ainda ausentes com o padrão (replica o acesso de hoje) — só
        // na abertura do form, nunca sobrescreve o que já está salvo.
        setPermissoes((atual) => {
          const completo = { ...atual };
          for (const a of r.data.acoes) {
            if (!(a.chave in completo)) completo[a.chave] = a.padrao;
          }
          return completo;
        });
      })
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar as permissões'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplica um perfil de uma vez (atalho) — o admin ainda pode destravar/travar cada caixinha
  // individual depois, não é exclusivo.
  function aplicarPerfil(chavePerfil) {
    const perfil = perfis.find((p) => p.chave === chavePerfil);
    if (!perfil) return;
    setPermissoes((atual) => ({ ...atual, ...perfil.valores }));
  }

  const superadminEscolhendoEmpresa =
    ehNovo && usuarioLogado.nivel === 1 && !empresaIdPreSelecionada && valores.nivel !== '1';

  useEffect(() => {
    if (superadminEscolhendoEmpresa && !empresas) {
      api
        .get('/empresas')
        .then((r) => setEmpresas(r.data))
        .catch((err) => toast.erro(err.message || 'Não foi possível carregar as empresas'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superadminEscolhendoEmpresa, empresas]);

  function atualizarCampo(nome, valor) {
    setValores((v) => ({ ...v, [nome]: valor }));
  }

  async function aoEnviar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const dados = paraApi(valores, ehNovo);
      if (!ehSuperadminAlvo) dados.permissoes = permissoes;
      await aoSalvar(dados);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Form onSubmit={aoEnviar}>
      {ehNovo && (
        <Alert variant="info">
          Senha inicial padrão: <strong>{senhaPadrao}</strong> — o usuário será obrigado a trocar no primeiro
          acesso.
        </Alert>
      )}

      <Row className="g-3">
        {ehNovo && (
          <Col md={6}>
            <Form.Group controlId="nivel">
              <Form.Label>Nível</Form.Label>
              <Form.Select value={valores.nivel} onChange={(e) => atualizarCampo('nivel', e.target.value)}>
                {usuarioLogado.nivel === 1 && !empresaIdPreSelecionada && (
                  <option value="1">Superadmin</option>
                )}
                <option value="2">Admin da imobiliária</option>
                <option value="3">Corretor</option>
              </Form.Select>
            </Form.Group>
          </Col>
        )}
        {superadminEscolhendoEmpresa && (
          <Col md={6}>
            <Form.Group controlId="empresaId">
              <Form.Label>Empresa</Form.Label>
              <Form.Select
                value={valores.empresaId}
                required
                onChange={(e) => atualizarCampo('empresaId', e.target.value)}
              >
                <option value="">Selecione...</option>
                {(empresas || []).map((emp) => (
                  <option key={emp.public_id} value={emp.public_id}>
                    {emp.e1_nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        )}
        {CAMPOS_USUARIO.map((campo) => {
          if (campo.soNaCriacao && !ehNovo) return null;
          return (
            <Col md={campo.tipo === 'checkbox' || campo.tipo === 'textarea' ? 12 : 6} key={campo.nome}>
              {campo.tipo === 'checkbox' ? (
                <Form.Check
                  type="checkbox"
                  id={campo.nome}
                  label={campo.rotulo}
                  checked={valores[campo.nome]}
                  onChange={(e) => atualizarCampo(campo.nome, e.target.checked)}
                />
              ) : (
                <Form.Group controlId={campo.nome}>
                  <Form.Label>{campo.rotulo}</Form.Label>
                  <Form.Control
                    as={campo.tipo === 'textarea' ? 'textarea' : undefined}
                    rows={campo.tipo === 'textarea' ? 3 : undefined}
                    type={campo.tipo === 'textarea' ? undefined : campo.tipo}
                    value={valores[campo.nome]}
                    required={campo.obrigatorio}
                    onChange={(e) => atualizarCampo(campo.nome, e.target.value)}
                  />
                </Form.Group>
              )}
            </Col>
          );
        })}
      </Row>

      {mostrarAcoes && acoes.length > 0 && (
        <fieldset className="mt-4">
          <legend className="h6">O que ele pode fazer</legend>
          <p className="text-muted small mb-2">
            Controla ação por ação (cadastrar, editar, excluir...) em cada parte do sistema — pensado pras
            funções reais de quem trabalha numa imobiliária (corretor completo, consultor que só atende,
            gerente que cuida da equipe toda). Admin da imobiliária (nível 2) e superadmin sempre têm acesso
            total, isso aqui só vale pra corretor.
          </p>
          <Form.Group className="mb-3" controlId="perfil" style={{ maxWidth: 420 }}>
            <Form.Label>Aplicar um perfil pronto (atalho — ainda dá pra ajustar cada item depois)</Form.Label>
            <Form.Select
              value={perfilSelecionado}
              onChange={(e) => {
                setPerfilSelecionado(e.target.value);
                if (e.target.value) aplicarPerfil(e.target.value);
              }}
            >
              <option value="">Escolher perfil...</option>
              {perfis.map((p) => (
                <option key={p.chave} value={p.chave}>
                  {p.rotulo}
                </option>
              ))}
            </Form.Select>
            {perfilSelecionado && (
              <Form.Text className="text-muted">{perfis.find((p) => p.chave === perfilSelecionado)?.descricao}</Form.Text>
            )}
          </Form.Group>
          {MODULOS_ACAO.map(({ chave: modulo, rotulo }) => {
            const doModulo = acoes.filter((a) => a.modulo === modulo);
            if (doModulo.length === 0) return null;
            return (
              <div key={modulo} className="mb-3">
                <div className="fw-bold small text-uppercase text-muted">{rotulo}</div>
                {doModulo.map((a) => (
                  <Form.Check
                    key={a.chave}
                    type="switch"
                    id={`acao-${a.chave}`}
                    label={a.rotulo}
                    checked={permissoes[a.chave] === true}
                    onChange={(e) => setPermissoes((atual) => ({ ...atual, [a.chave]: e.target.checked }))}
                  />
                ))}
              </div>
            );
          })}
        </fieldset>
      )}

      {!ehSuperadminAlvo && recursos.length > 0 && (
        <fieldset className="mt-4">
          <legend className="h6">Recursos adicionais</legend>
          <p className="text-muted small mb-2">
            Tudo começa desligado. O admin da imobiliária também pode ser corretor — libere aqui para ele
            também, se for o caso. O recurso só funciona se a EMPRESA também o tiver no plano (o superadmin
            liga na tela da empresa).
          </p>
          {recursos.map((p) => (
            <Form.Check
              key={p.chave}
              type="switch"
              id={`perm-${p.chave}`}
              label={p.rotulo}
              checked={permissoes[p.chave] === true}
              onChange={(e) => setPermissoes((atual) => ({ ...atual, [p.chave]: e.target.checked }))}
            />
          ))}
        </fieldset>
      )}

      {/* Senha (só na edição de OUTRO usuário): define uma senha nova; a atual não existe em texto, só o hash */}
      {!ehNovo && usuarioInicial?.public_id && usuarioInicial.public_id !== usuarioLogado?.id && (
        <div className="border rounded p-3 mt-3 bg-light">
          <div className="fw-semibold mb-1">Senha</div>
          <div className="text-muted small mb-2">
            A senha atual não pode ser exibida: por segurança ela é guardada criptografada (nem o sistema consegue ler). Aqui você
            define uma <strong>nova</strong> — use o olho para conferir o que está digitando.
          </div>
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Group controlId="nova-senha-usuario">
                <Form.Label>Nova senha</Form.Label>
                <CampoSenha value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} minLength={senhaMinima} autoComplete="new-password" placeholder={`Mínimo de ${senhaMinima} caracteres`} />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button type="button" variant="outline-warning" disabled={definindoSenha || novaSenha.length < senhaMinima} onClick={definirNovaSenha}>
                {definindoSenha ? <Spinner animation="border" size="sm" /> : 'Definir nova senha'}
              </Button>
            </Col>
          </Row>
          <Form.Check
            className="mt-2"
            type="checkbox"
            id="trocar-no-proximo-acesso"
            label="Pedir para o usuário trocar essa senha no próximo acesso"
            checked={trocarNoProximo}
            onChange={(e) => setTrocarNoProximo(e.target.checked)}
          />
          <Form.Text className="text-muted">A senha vale na hora e quem estiver logado com a antiga é desconectado. Não depende do botão Salvar.</Form.Text>
        </div>
      )}

      <Button type="submit" variant="primary" className="mt-3" disabled={salvando}>
        {salvando ? <Spinner animation="border" size="sm" /> : 'Salvar'}
      </Button>
    </Form>
  );
}
